import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { User, Role, Permission } from '../types'
import { authApi } from './api/authApi'

interface AuthState {
	user: User | null
	token: string | null
	isAuthenticated: boolean
	isInitialized: boolean
	roles: Role[]
	permissions: Permission[]
}

const getTokenFromStorage = (): string | null => {
	return localStorage.getItem('access_token')
}

const initialState: AuthState = {
	user: null,
	token: getTokenFromStorage(),
	isAuthenticated: false,
	isInitialized: false,
	roles: [],
	permissions: [],
}

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setCredentials: (
			state,
			action: PayloadAction<{ user: User; token: string }>
		) => {
			state.user = action.payload.user
			state.token = action.payload.token
			state.isAuthenticated = true
			localStorage.setItem('access_token', action.payload.token)
		},
		logout: (state) => {
			state.user = null
			state.token = null
			state.isAuthenticated = false
			state.roles = []
			state.permissions = []
			localStorage.removeItem('access_token')
		},
		setInitialized: (state, action: PayloadAction<boolean>) => {
			state.isInitialized = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				authApi.endpoints.login.matchFulfilled,
				(state, action) => {
					const { user, access_token } = action.payload
					state.user = user
					state.token = access_token
					state.isAuthenticated = true
					localStorage.setItem('access_token', access_token)
				}
			)
			.addMatcher(
				authApi.endpoints.getMe.matchFulfilled,
				(state, action) => {
					state.user = action.payload.user
					state.roles = action.payload.roles
					state.permissions = action.payload.permissions
					state.isAuthenticated = true
					state.isInitialized = true
				}
			)
			.addMatcher(
				authApi.endpoints.getMe.matchRejected,
				(state, action) => {
					if (action.error.name !== 'ConditionError') {
						state.user = null
						state.token = null
						state.isAuthenticated = false
						state.roles = []
						state.permissions = []
						localStorage.removeItem('access_token')
					}
					state.isInitialized = true
				}
			)
			.addMatcher(
				authApi.endpoints.logout.matchFulfilled,
				(state) => {
					state.user = null
					state.token = null
					state.isAuthenticated = false
					state.roles = []
					state.permissions = []
					localStorage.removeItem('access_token')
				}
			)
			.addMatcher(
				authApi.endpoints.logoutAll.matchFulfilled,
				(state) => {
					state.user = null
					state.token = null
					state.isAuthenticated = false
					state.roles = []
					state.permissions = []
					localStorage.removeItem('access_token')
				}
			)
	},
})

export const { setCredentials, logout, setInitialized } = authSlice.actions
export default authSlice.reducer

